/**
 * Inbox variant shape and bounds, kept OUT of the .tsx so the editor module
 * exports only its component — `react-refresh/only-export-components` is a
 * lint error under the portal's `--max-warnings 0` gate.
 */

/** Nova column bounds (contract §4). Over the bound fails THAT recipient. */
export const INBOX_HEADLINE_MAX = 255;
export const INBOX_PREVIEW_MAX = 500;

export const INBOX_CATEGORIES = ['Promotion', 'Message'] as const;
export type InboxCategory = (typeof INBOX_CATEGORIES)[number];

export interface InboxVariant {
  headline: string;
  preview: string;
  fullMessage: string;
  category: InboxCategory;
  /**
   * Optional hero image. Empty means "no image" and is omitted from the
   * payload; when present Nova requires https (contract §4).
   *
   * `iconType` is deliberately NOT exposed. Nova stores it as free-text
   * varchar(50) with no enum and no validation, and the vocabulary the mobile
   * app actually renders is not discoverable from this repo — a free-text box
   * would invite values that silently render nothing.
   */
  imageUrl: string;
  /**
   * Optional tap-through destination. Either an external `https://` URL or an
   * internal app route (`/rewards`). Empty means "not tappable" and is omitted
   * from the payload. Neither `//host` nor `/\host` is a route — both resolve
   * to an external host in a webview, so both are refused here.
   */
  ctaUrl: string;
  /** Optional promotional video. https only, same rule as `imageUrl`. */
  videoUrl: string;
  companionPush: {
    enabled: boolean;
    title: string;
    body: string;
    imageUrl: string;
  };
}

export const INBOX_URL_MAX = 500;

export const EMPTY_INBOX_VARIANT: InboxVariant = {
  headline: '',
  preview: '',
  fullMessage: '',
  category: 'Message',
  imageUrl: '',
  ctaUrl: '',
  videoUrl: '',
  companionPush: { enabled: false, title: '', body: '', imageUrl: '' },
};

/**
 * MUST stay byte-identical to `NOTIFICATION_CTA_URL_PATTERN` in
 * `apps/api/src/common/constants/notification-url.constants.ts` (and to
 * `INBOX_CTA_URL_PATTERN` in strata-api's `campaigns.service.ts`). Three copies
 * exist because the three apps share no package; change one, change all three.
 *
 * `//host` and `/\host` are both refused — the WHATWG URL parser treats `\` as
 * `/` in the authority position, so `/\evil.com` resolves to an external host.
 */
const INBOX_CTA_URL_PATTERN = /^(?:https:\/\/\S+|\/(?![/\\])[^\s\\]*)$/;

/** `https://…` or an app-internal absolute path, but never `//host` / `/\host`. */
export function isInboxCtaUrlValid(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  return INBOX_CTA_URL_PATTERN.test(trimmed);
}

/**
 * A media URL must PARSE as https with a host, not merely start with `https://`
 * — strata-api and nova both apply the stricter rule, so accepting `https://`
 * here would only defer the rejection to submit time.
 */
export function isInboxMediaUrlValid(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  if (/\s/.test(trimmed)) {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:' && parsed.hostname.length > 0;
  } catch {
    return false;
  }
}

const INBOX_IMAGE_FILE_PATTERN = /\.(?:jpe?g|png|webp)$/i;
const INBOX_VIDEO_FILE_PATTERN = /\.(?:mp4|webm)$/i;

function isInboxMediaUrlOfType(value: string, filePattern: RegExp): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  if (!isInboxMediaUrlValid(trimmed)) {
    return false;
  }
  return filePattern.test(new URL(trimmed).pathname);
}

/** A public HTTPS URL whose pathname names a supported inbox image file. */
export function isInboxImageUrlValid(value: string): boolean {
  return isInboxMediaUrlOfType(value, INBOX_IMAGE_FILE_PATTERN);
}

/** A public HTTPS URL whose pathname names a supported inbox video file. */
export function isInboxVideoUrlValid(value: string): boolean {
  return isInboxMediaUrlOfType(value, INBOX_VIDEO_FILE_PATTERN);
}

/**
 * Bounds the TEMPLATE, which is what the operator can act on. A rendered value
 * can still exceed the column for one recipient with a very long first name;
 * Nova fails that recipient and commits the rest, so this is a compose-time
 * gate, not a delivery guarantee.
 */
export function inboxVariantError(variant: InboxVariant): string | null {
  if (
    !variant.headline.trim() ||
    !variant.preview.trim() ||
    !variant.fullMessage.trim()
  ) {
    return 'Please complete the in-app inbox headline, preview and full message';
  }
  if (variant.headline.length > INBOX_HEADLINE_MAX) {
    return `Inbox headline exceeds ${INBOX_HEADLINE_MAX} characters`;
  }
  if (variant.preview.length > INBOX_PREVIEW_MAX) {
    return `Inbox preview exceeds ${INBOX_PREVIEW_MAX} characters`;
  }
  if (!isInboxImageUrlValid(variant.imageUrl)) {
    return 'Inbox image URL must be a public HTTPS JPG, JPEG, PNG or WebP file';
  }
  if (!isInboxCtaUrlValid(variant.ctaUrl)) {
    return 'Inbox link must start with https:// or with / for an in-app screen';
  }
  if (variant.ctaUrl.trim().length > INBOX_URL_MAX) {
    return `Inbox link exceeds ${INBOX_URL_MAX} characters`;
  }
  if (!isInboxVideoUrlValid(variant.videoUrl)) {
    return 'Inbox video URL must be a public HTTPS MP4 or WebM file';
  }
  if (variant.videoUrl.trim().length > INBOX_URL_MAX) {
    return `Inbox video URL exceeds ${INBOX_URL_MAX} characters`;
  }
  if (variant.imageUrl.trim().length > INBOX_URL_MAX) {
    return `Inbox image URL exceeds ${INBOX_URL_MAX} characters`;
  }
  const hasMedia = Boolean(variant.imageUrl.trim() || variant.videoUrl.trim());
  if (hasMedia && variant.category !== 'Promotion') {
    return 'Inbox messages with media must use the Promotion category';
  }
  if (!hasMedia && variant.category !== 'Message') {
    return 'Inbox messages without media must use the Message category';
  }
  if (variant.companionPush?.enabled) {
    const { title, body, imageUrl } = variant.companionPush;
    if (!title.trim() || !body.trim()) {
      return 'Please enter a companion push title and message';
    }
    if (title.length > 250)
      return 'Companion push title exceeds 250 characters';
    if (body.length > 2000)
      return 'Companion push message exceeds 2000 characters';
    if (imageUrl && !isInboxImageUrlValid(imageUrl)) {
      return 'Companion push image must be a public HTTPS JPG, JPEG, PNG or WebP file';
    }
    if (imageUrl.length > INBOX_URL_MAX) {
      return `Companion push image URL exceeds ${INBOX_URL_MAX} characters`;
    }
    if (/{{(?:first_name|surname)}}/.test(`${title}\n${body}`)) {
      return 'Companion push cannot use {{first_name}} or {{surname}} because one push is shared by the batch. {{brand}} is supported.';
    }
  }
  return null;
}

export function isInboxVariantComplete(variant: InboxVariant): boolean {
  return inboxVariantError(variant) === null;
}
